/**
 * Top-left minimap.
 *
 * The static map layer is rendered ONCE: an orthographic top-down camera into a
 * WebGLRenderTarget with a height-shaded material override, read back into a 2D
 * canvas. Per frame we only composite that bitmap (one rotated drawImage) plus a
 * handful of blips inside a circular clip — which is what keeps this inside the
 * HUD frame budget.
 *
 * If the GL readback is unavailable for any reason we fall back to a
 * deterministic procedural grid so the HUD never shows a hole.
 */
import * as THREE from 'three';
import { COL, rgba, clamp01, fitCanvas, clearCanvas, devicePx, ease } from './Theme.js';
import { label } from './Glyphs.js';
import { makeRng } from '../core/Rand.js';

export const SIZE = 208;          // css px, square
const RT = 512;                   // static layer resolution
const TAU = Math.PI * 2;

export class Minimap {
  constructor(root) {
    this.cv = document.createElement('canvas');
    this.cv.className = 'hud-c hud-minimap';
    root.appendChild(this.cv);
    this.g = fitCanvas(this.cv, SIZE, SIZE);

    this.map = document.createElement('canvas');   // offscreen static layer
    this.map.width = this.map.height = RT;
    this.mapG = this.map.getContext('2d');
    this.ready = false;
    this.minX = -30; this.minZ = -30; this.span = 60;
    this.windowMetres = 62;
    this.built = false;
  }

  resize(dpr = devicePx()) {
    this.g = fitCanvas(this.cv, SIZE, SIZE, dpr);
  }

  /** px on the map bitmap per world metre */
  get mapScale() { return RT / this.span; }
  /** display px per world metre */
  get ppm() { return (SIZE * 0.9) / this.windowMetres; }

  /* --------------------------------------------------------------- build */

  build(ctx) {
    this.built = true;
    try {
      this._renderStatic(ctx);
      this.ready = true;
    } catch (err) {
      console.warn('[ui] minimap GL bake unavailable, using procedural fallback:', err?.message || err);
      this._proceduralFallback(ctx);
      this.ready = true;
    }
  }

  _bounds(ctx) {
    const b = new THREE.Box3();
    const src = ctx.level?.navBounds || ctx.level?.bounds;
    if (src && src.isBox3 && !src.isEmpty()) b.copy(src);
    else if (ctx.level?.root) b.setFromObject(ctx.level.root);
    else b.set(new THREE.Vector3(-30, 0, -30), new THREE.Vector3(30, 6, 30));
    if (b.isEmpty()) b.set(new THREE.Vector3(-30, 0, -30), new THREE.Vector3(30, 6, 30));
    const sx = b.max.x - b.min.x, sz = b.max.z - b.min.z;
    const span = Math.max(sx, sz) * 1.06 + 4;
    const cx = (b.min.x + b.max.x) / 2, cz = (b.min.z + b.max.z) / 2;
    this.span = span;
    this.minX = cx - span / 2;
    this.minZ = cz - span / 2;
    return { b, cx, cz, span };
  }

  _renderStatic(ctx) {
    const { b, cx, cz, span } = this._bounds(ctx);
    const renderer = ctx.renderer;
    const scene = ctx.scene;
    if (!renderer || !scene) throw new Error('no renderer/scene');

    const top = Math.max(b.max.y + 8, 20);
    const cam = new THREE.OrthographicCamera(-span / 2, span / 2, span / 2, -span / 2, 0.1, top + 400);
    cam.position.set(cx, top + 60, cz);
    cam.up.set(0, 0, -1);
    cam.lookAt(cx, b.min.y - 1, cz);
    cam.updateMatrixWorld(true);

    // Clamp the height ramp to a ~12 m window above the floor: a single tall
    // silo must not flatten every wall in the level into the same value.
    const lo = b.min.y;
    const hi = Math.min(Math.max(b.min.y + 4, b.max.y), b.min.y + 12);
    const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false });
    mat.onBeforeCompile = (sh) => {
      sh.uniforms.uLo = { value: lo };
      sh.uniforms.uHi = { value: hi };
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying float vWH;')
        .replace('#include <fog_vertex>', '#include <fog_vertex>\nvWH = (modelMatrix * vec4(transformed, 1.0)).y;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying float vWH;\nuniform float uLo;\nuniform float uHi;')
        .replace('#include <dithering_fragment>',
          '#include <dithering_fragment>\n'
          + 'float mmT = clamp((vWH - uLo) / max(0.001, uHi - uLo), 0.0, 1.0);\n'
          + 'vec3 mmC = mix(vec3(0.080,0.102,0.115), vec3(0.52,0.58,0.60), pow(mmT, 0.62));\n'
          + 'gl_FragColor = vec4(mmC, 1.0);');
    };
    mat.customProgramCacheKey = () => 'hud-minimap';

    // Hide anything sky-sized so the ortho pass sees only the level shell.
    const hidden = [];
    scene.traverse((o) => {
      if (!o.visible || !o.isMesh) return;
      const n = (o.name || '') + '|' + (o.parent?.name || '');
      const big = o.geometry?.boundingSphere?.radius > 400
        || (o.geometry && (o.geometry.computeBoundingSphere(), o.geometry.boundingSphere?.radius > 400));
      if (big || /sky|backdrop|env|cloud|sun|viewmodel/i.test(n)) {
        o.visible = false;
        hidden.push(o);
      }
    });

    const target = new THREE.WebGLRenderTarget(RT, RT, {
      depthBuffer: true, stencilBuffer: false,
      type: THREE.UnsignedByteType, format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    });

    const prevTarget = renderer.getRenderTarget();
    const prevOverride = scene.overrideMaterial;
    const prevBg = scene.background;
    const prevFog = scene.fog;
    const prevShadow = renderer.shadowMap.enabled;
    let pixels = null;
    try {
      scene.overrideMaterial = mat;
      scene.background = null;
      scene.fog = null;
      renderer.shadowMap.enabled = false;
      renderer.setRenderTarget(target);
      renderer.clear(true, true, false);
      renderer.render(scene, cam);
      pixels = new Uint8Array(RT * RT * 4);
      renderer.readRenderTargetPixels(target, 0, 0, RT, RT, pixels);
    } finally {
      renderer.setRenderTarget(prevTarget);
      scene.overrideMaterial = prevOverride;
      scene.background = prevBg;
      scene.fog = prevFog;
      renderer.shadowMap.enabled = prevShadow;
      for (const o of hidden) o.visible = true;
      target.dispose();
      mat.dispose();
    }

    // GL rows are bottom-up; the map image wants +Z downward, which matches the
    // ortho camera's up = -Z once we flip.
    const img = this.mapG.createImageData(RT, RT);
    const d = img.data;
    let solid = 0;
    for (let y = 0; y < RT; y++) {
      const src = (RT - 1 - y) * RT * 4;
      const dst = y * RT * 4;
      for (let x = 0; x < RT * 4; x += 4) {
        const r = pixels[src + x], gg = pixels[src + x + 1], bb = pixels[src + x + 2];
        const lit = r + gg + bb > 24;
        if (lit) solid++;
        d[dst + x] = r; d[dst + x + 1] = gg; d[dst + x + 2] = bb;
        d[dst + x + 3] = lit ? 255 : 0;
      }
    }
    if (solid < RT * RT * 0.01) throw new Error('ortho bake produced an empty map');
    this.mapG.putImageData(img, 0, 0);
    this._postProcessMap();
  }

  /** Add a subtle contour/edge pass so the footprint silhouettes read. */
  _postProcessMap() {
    const g = this.mapG;
    g.save();
    g.globalCompositeOperation = 'source-atop';
    const grid = 4; // metres
    const step = (RT / this.span) * grid;
    g.strokeStyle = 'rgba(255,255,255,0.055)';
    g.lineWidth = 1;
    for (let i = 0; i * step < RT; i++) {
      const p = Math.round(i * step) + 0.5;
      g.beginPath(); g.moveTo(p, 0); g.lineTo(p, RT); g.stroke();
      g.beginPath(); g.moveTo(0, p); g.lineTo(RT, p); g.stroke();
    }
    g.restore();
  }

  _proceduralFallback(ctx) {
    this._bounds(ctx);
    const g = this.mapG;
    const r = makeRng(0x4d1f0a);
    g.clearRect(0, 0, RT, RT);
    g.fillStyle = '#0C1114';
    g.fillRect(0, 0, RT, RT);
    for (let i = 0; i < 46; i++) {
      const w = r.range(24, 96), h = r.range(24, 96);
      g.fillStyle = `rgba(${(48 + r() * 40) | 0},${(58 + r() * 42) | 0},${(62 + r() * 44) | 0},1)`;
      g.fillRect(r.range(10, RT - w - 10) | 0, r.range(10, RT - h - 10) | 0, w | 0, h | 0);
    }
    this._postProcessMap();
  }

  /* ----------------------------------------------------------- composite */

  worldToScreen(wx, wz, camX, camZ, yaw) {
    const p = this.ppm;
    const dx = (wx - camX) * p, dy = (wz - camZ) * p;
    const c = Math.cos(yaw), s = Math.sin(yaw);
    return [SIZE / 2 + dx * c - dy * s, SIZE / 2 + dx * s + dy * c];
  }

  draw(s) {
    const g = this.g;
    clearCanvas(g);
    const cx = SIZE / 2, cy = SIZE / 2, R = SIZE / 2 - 8;

    /* backing disc */
    g.beginPath(); g.arc(cx, cy, R, 0, TAU);
    g.fillStyle = 'rgba(4,7,10,0.72)';
    g.fill();

    /* --------------------------------------------------- static map layer */
    if (this.ready) {
      g.save();
      g.beginPath(); g.arc(cx, cy, R, 0, TAU); g.clip();
      const sc = this.ppm / this.mapScale;
      const px = (s.camX - this.minX) * this.mapScale;
      const py = (s.camZ - this.minZ) * this.mapScale;
      g.translate(cx, cy);
      g.rotate(s.yaw);
      g.scale(sc, sc);
      g.translate(-px, -py);
      g.globalAlpha = 0.95;
      g.drawImage(this.map, 0, 0);
      g.restore();
    }

    /* -------------------------------------------------------------- blips */
    g.save();
    g.beginPath(); g.arc(cx, cy, R, 0, TAU); g.clip();

    for (const b of s.blips) {
      const [x, y] = this.worldToScreen(b.x, b.z, s.camX, s.camZ, s.yaw);
      const age = clamp01(b.t / b.life);
      const a = 1 - ease.outCubic(age);
      if (a <= 0.02) continue;
      // firing blip: a red dot with an expanding shock ring
      const rr = 3 + ease.outQuart(age) * 12;
      g.strokeStyle = rgba(COL.enemy, a * 0.5);
      g.lineWidth = 1.4;
      g.beginPath(); g.arc(x, y, rr, 0, TAU); g.stroke();
      g.fillStyle = rgba('#000000', a * 0.6);
      g.beginPath(); g.arc(x, y, 4.4, 0, TAU); g.fill();
      g.fillStyle = rgba(COL.enemy, a);
      g.beginPath(); g.arc(x, y, 3.2, 0, TAU); g.fill();
    }

    for (const m of s.mapMarkers) {
      const [x, y] = this.worldToScreen(m.x, m.z, s.camX, s.camZ, s.yaw);
      if (m.kind === 'ally') {
        g.save();
        g.translate(x, y);
        g.rotate(s.yaw - (m.yaw ?? 0));
        g.fillStyle = rgba('#000000', 0.7);
        tri(g, 7.4);
        g.fillStyle = COL.ally;
        tri(g, 5.6);
        g.restore();
      } else if (m.kind === 'objective') {
        g.fillStyle = rgba('#000000', 0.7);
        diamond(g, x, y, 8.4); g.fill();
        g.fillStyle = rgba(COL.amber, 0.95);
        diamond(g, x, y, 6.6); g.fill();
        label(g, m.label, x, y - 5.5, {
          size: 10, weight: 700, tracking: 0.02, align: 'center', color: '#1A1206', halo: null,
        });
      }
    }

    /* ------------------------------------------------------ player arrow */
    g.fillStyle = rgba('#000000', 0.75);
    tri2(g, cx, cy, 10.5);
    g.fillStyle = COL.white;
    tri2(g, cx, cy, 8);
    // cone of vision
    const cone = g.createRadialGradient(cx, cy, 2, cx, cy, 46);
    cone.addColorStop(0, 'rgba(237,242,244,0.20)');
    cone.addColorStop(1, 'rgba(237,242,244,0)');
    g.fillStyle = cone;
    g.beginPath();
    g.moveTo(cx, cy);
    g.arc(cx, cy, 46, -Math.PI / 2 - 0.52, -Math.PI / 2 + 0.52);
    g.closePath();
    g.fill();

    g.restore();

    /* --------------------------------------------------------- bezel ring */
    g.lineWidth = 3;
    g.strokeStyle = 'rgba(0,0,0,0.55)';
    g.beginPath(); g.arc(cx, cy, R + 1.5, 0, TAU); g.stroke();
    g.lineWidth = 1;
    g.strokeStyle = rgba(COL.white, 0.30);
    g.beginPath(); g.arc(cx, cy, R + 0.5, 0, TAU); g.stroke();
    // quarter ticks
    for (let i = 0; i < 4; i++) {
      const a = i * Math.PI / 2 - Math.PI / 2;
      g.strokeStyle = rgba(COL.white, 0.42);
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(cx + Math.cos(a) * (R - 1), cy + Math.sin(a) * (R - 1));
      g.lineTo(cx + Math.cos(a) * (R + 4), cy + Math.sin(a) * (R + 4));
      g.stroke();
    }
    // north pip rides the ring
    const na = Math.atan2(-Math.cos(s.yaw), Math.sin(s.yaw));
    const nx = cx + Math.cos(na) * (R - 9), ny = cy + Math.sin(na) * (R - 9);
    g.fillStyle = rgba('#000000', 0.6);
    g.beginPath(); g.arc(nx, ny, 7.5, 0, TAU); g.fill();
    label(g, 'N', nx, ny - 6, { size: 11, weight: 700, tracking: 0, align: 'center', color: COL.amber, halo: null });
  }
}

function tri(g, s) {
  g.beginPath();
  g.moveTo(0, -s); g.lineTo(s * 0.72, s * 0.78); g.lineTo(0, s * 0.34); g.lineTo(-s * 0.72, s * 0.78);
  g.closePath(); g.fill();
}
function tri2(g, x, y, s) {
  g.save(); g.translate(x, y); tri(g, s); g.restore();
}
function diamond(g, x, y, s) {
  g.beginPath();
  g.moveTo(x, y - s); g.lineTo(x + s, y); g.lineTo(x, y + s); g.lineTo(x - s, y);
  g.closePath();
}
